import React, { FC, useEffect, useState, FormEvent, ChangeEvent } from 'react';
import { Entry } from 'contentful';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import Loader from '../Loader';
import TextInput from './TextInput';
import SelectInput from './SelectInput';
import Checkbox from './CheckboxInput';
import {
  StyledButton,
  ButtonContainer,
  CampaignMessaging,
  ErrorText,
  FormContainer,
  NameFieldsContainer
} from './Styles';

interface EmailCaptureProps extends Entry {
  fields: {
    apiKey: string;
    activeCampaignMessage: any;
    campaignSourceId: string;
    ctaText: string;
    dropdownOneLabel: string;
    dropdownOneOptions: string[];
    dropdownTwoLabel: string;
    dropdownTwoOptions: string[];
    eligibilityLabel: string;
    emailLabel: string;
    firstNameLabel: string;
    lastNameLabel: string;
    marketingOptInLabel: string;
    marketingOptInLocales: string[];
    successMessage: any;
  };
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  dropdownOneValue: string;
  dropdownTwoValue: string;
  eligibility: boolean;
  marketingOptIn: boolean;
}

const initialFormData: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  dropdownOneValue: '',
  dropdownTwoValue: '',
  eligibility: false,
  marketingOptIn: false,
};

const EmailCapture: FC<EmailCaptureProps> = ({ fields }) => {
  const {
    activeCampaignMessage,
    apiKey,
    campaignSourceId,
    ctaText,
    dropdownOneLabel,
    dropdownOneOptions,
    dropdownTwoLabel,
    dropdownTwoOptions,
    eligibilityLabel,
    emailLabel,
    firstNameLabel,
    lastNameLabel,
    marketingOptInLabel,
    marketingOptInLocales,
    successMessage,
  } = fields;

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ error: string | null; success: boolean }>({
    error: null,
    success: false,
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [countryCode, setCountryCode] = useState('US');

  useEffect(() => {
    const selectedCountryCode = (document.getElementById('country-code') as HTMLInputElement)?.value;
    setCountryCode(selectedCountryCode || 'US');
  }, []);

  const showMarketingOptIn = marketingOptInLocales.includes(countryCode);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, type, value } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    const addError = (field: keyof FormData, message: string) => {
      newErrors[field] = message;
    };

    if (!formData.firstName.trim()) addError('firstName', 'First name is required.');
    if (!formData.lastName.trim()) addError('lastName', 'Last name is required.');

    if (!formData.email.trim()) {
      addError('email', 'Email is required.');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      addError('email', 'Please enter a valid email address.');
    }

    if (!formData.dropdownOneValue) {
      addError('dropdownOneValue', 'Please select an option.');
    }

    if (!formData.eligibility) {
      addError('eligibility', 'You must agree to the eligibility criteria.');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSubmitError(null);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    if (!formData.marketingOptIn) {
      setSubmitStatus({ error: null, success: true });
      setFormData(initialFormData);
      setErrors({});
      setLoading(false);
      return;
    }

    const payload = {
      email: formData.email,
      dataFields: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        dropdownOne: formData.dropdownOneValue,
        dropdownTwo: formData.dropdownTwoValue || '',
        marketingOptIn: formData.marketingOptIn,
        source: campaignSourceId,
        countryCode,
      },
    };

    try {
      const response = await fetch('https://api.iterable.com/api/users/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': apiKey,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      setSubmitStatus({ error: null, success: true });
      setFormData(initialFormData);
      setErrors({});
    } catch (error) {
      console.error('Error during form submission:', error);
      setSubmitStatus({
        error: 'There was an error submitting the form. Please try again later.',
        success: false,
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitStatus.success) {
    return <CampaignMessaging>{documentToReactComponents(successMessage)}</CampaignMessaging>;
  }

  return (
    <FormContainer id="EmailCapture" onSubmit={handleSubmit}>
      {loading ? (
        <Loader />
      ) : (
        <>
          <CampaignMessaging>{documentToReactComponents(activeCampaignMessage)}</CampaignMessaging>

          <NameFieldsContainer>
            <TextInput
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              label={firstNameLabel}
              required
            />
            <TextInput
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              label={lastNameLabel}
              required
            />
          </NameFieldsContainer>

          <TextInput
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            label={emailLabel}
            isEmailInput
            required
          />

          <SelectInput
            id="dropdownOneValue"
            name="dropdownOneValue"
            value={formData.dropdownOneValue}
            onChange={handleChange}
            label={dropdownOneLabel}
            options={dropdownOneOptions}
            required
          />

          <SelectInput
            id="dropdownTwoValue"
            name="dropdownTwoValue"
            value={formData.dropdownTwoValue}
            onChange={handleChange}
            label={dropdownTwoLabel}
            options={dropdownTwoOptions}
          />

          <Checkbox
            id="eligibility"
            name="eligibility"
            checked={formData.eligibility}
            onChange={handleChange}
            label={eligibilityLabel}
            required
          />

          {showMarketingOptIn && (
            <Checkbox
              id="marketingOptIn"
              name="marketingOptIn"
              checked={formData.marketingOptIn}
              onChange={handleChange}
              label={marketingOptInLabel}
            />
          )}

          {submitError && <ErrorText>{submitError}</ErrorText>}

          <ButtonContainer>
            <StyledButton type="submit" disabled={loading}>
              {ctaText}
            </StyledButton>
          </ButtonContainer>
        </>
      )}
    </FormContainer>
  );
};

export default EmailCapture;
